/**
 * Sun shadows: **one** cascade, 2048, world geometry only.
 *
 * ## Why one
 *
 * The old path split the view frustum three or four ways so a 2 m crate under a 4.5-degree sun
 * could throw a 25 m shadow with ~1 cm texels near the camera. Every split is a whole extra
 * render pass: another `updateMatrixWorld` walk of the scene graph, another `_projectObject`
 * traversal, another set of draws. Measured on the owner's 4080 (WEBGPU-NOTES §6) three
 * cascades were ~45 ms of a frame that should have been 8. PERF-BUDGET's rule 2 is now the
 * rule: never let a pass count scale with content, and one sun cascade at ~10 draws is the
 * whole shadow budget.
 *
 * What buys the quality back is that the tier no longer needs to cover 220 m of grazing
 * shadow: `CASCADE_FAR` is 120 m and everything past it is fog and silhouette.
 *
 * ## Why characters are not in it
 *
 * They are not casters at all any more. A skinned rig is re-skinned once per shadow pass on
 * top of its colour pass, and the crowd budget is 300 of them; `render/crowd.ts` draws a blob
 * shadow per character out of one instanced decal mesh instead. So the sun's shadow camera
 * does not enable `RIG_LAYER` in either path, and `StillsunCSM` no longer has a near-cascade
 * special case for it. `RIG_LAYER` still exists in `renderer.ts` and rigs still render — they
 * just do not cast.
 *
 * ## Why this file and not a `sun.shadow` tweak in scene.ts
 *
 * The cascade needs a per-frame step and scene.ts has no frame step; `onBeforeFrame` in
 * renderer.ts does. Three things happen there: `updateFrustums()` when the camera's FOV or
 * aspect changes, the bias's light-vector uniform following the moving key (`render/sky.ts`
 * sweeps a full arc every four real hours and hands over to the moon at night — see
 * `SUN_DIR_EPSILON_COS`), and the night gate that stops the pass while the key is the moon
 * (`SHADOW_KEY_OFF`). It also needs the precompile hook below.
 *
 * Cache-key hygiene: nothing here changes per frame except uniforms and plain JS flags
 * (`reference` nodes onto the cascade shadow and its camera; `LightShadow.autoUpdate`).
 * `updateFrustums()` only rewrites ortho extents. Nothing ever touches a light's `visible` or
 * the light count — see `SHADOW_KEY_OFF`.
 */
import { Vector3, type DirectionalLight } from 'three/webgpu';
import * as TSL from 'three/tsl';
import { CSMShadowNode } from 'three/addons/csm/CSMShadowNode.js';
import { onBeforeFrame, onPrecompile, queryDisabled, type QualityTier } from './renderer.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyNode = any;
type NodeFn = (...args: AnyNode[]) => AnyNode;
const { float, clamp, min, sqrt, uniform } = TSL as unknown as Record<
  'float' | 'clamp' | 'min' | 'sqrt' | 'uniform',
  NodeFn
>;
const normalWorld: AnyNode = TSL.normalWorld;

interface ShadowParams {
  cascades: number;
  mapSize: number;
  /** Metres. Beyond this there is no sun shadow. */
  maxFar: number;
  /** Cascade far distances, metres, ascending; the last must equal `maxFar`. */
  splits: readonly number[];
  /** PCF disc radius, shadow-map texels. */
  radius: number;
}

/**
 * One row, shared by every tier. The tiers differ in pixel ratio and in material detail, not
 * in how many shadow passes the frame pays for — a low-end machine and a 4080 both render
 * exactly one cascade, because the cost that killed the old path was per-pass CPU, which the
 * weaker machine can afford least.
 *
 * `CASCADE_FAR = 120 m`, and the number is picked rather than inherited:
 *
 * - The third-person camera sits a few metres behind the character, so 120 m of shadowed world
 *   is roughly a city block in every direction — past that a stylised, flat-shaded silhouette
 *   against fog carries the depth cue on its own.
 * - `CSMShadowNode` fits the cascade to the view frustum's bounding box, which at a 75-degree
 *   FOV and 120 m is a ~170 m ortho box. Over a 2048 map that is ~8 cm per texel — better than
 *   the old *middle* cascade and a third of the far one's 24 cm, which is what made the far
 *   cascade's bias arithmetic (WEBGPU-NOTES §3) so unforgiving.
 * - It is also comfortably inside the 600 m near/far range the bias is scaled against, so the
 *   depth precision argument in `installCascadeBias` holds with margin.
 *
 * The `splits` array must end at `maxFar`: `customSplitsCallback` pushes `min(1, s / far)`, so
 * a single split of 120 against `maxFar` 120 yields `breaks = [1.0]` — the one cascade covering
 * the whole range. A mismatch here fails silently, with the cascade quietly covering the wrong
 * distance.
 */
const CASCADE_FAR = 120;
const PARAMS: ShadowParams = {
  cascades: 1,
  mapSize: 2048,
  maxFar: CASCADE_FAR,
  splits: [CASCADE_FAR],
  // No `fade`: fading exists to hide the seam where two cascades meet, and there is no seam.
  // No contact hardening: 24 depth compares per receiver pixel bought a soft penumbra on a
  // path that no longer has a PBR surface to show it off.
  radius: 1.6,
};

/**
 * Slope-scaled depth bias, per pixel (see `installCascadeBias`).
 *
 * Why a constant bias cannot work here: a shadow-map texel is ~8 cm of ground, and on a
 * horizontal receiver under a 4.5 deg sun that texel spans 0.08 / tan(4.5) = 1.0 m of
 * light-space depth. A constant that covers that on flat ground peter-pans everything else.
 *
 * The bias is therefore built from the receiver's slope: half a texel of guaranteed cover plus
 * BIAS_SLOPE_TEXELS texels times tan(acos(N.L)), all in world metres of light-space depth,
 * then divided by the cascade camera's depth range to land in the NDC units three adds to the
 * receiver depth. tan is clamped at BIAS_SLOPE_TAN_MAX: at exactly grazing incidence it would
 * go to infinity, and past 16 (N.L below 0.06) the surface receives no direct light worth
 * shadowing; 8 was measured a hair short on the most grazing dune faces (thin residual
 * stripes). The texel size is a uniform, refreshed whenever the frustum is recomputed.
 */
const BIAS_BASE_TEXELS = 0.5;
const BIAS_SLOPE_TEXELS = 1.5;
const BIAS_SLOPE_TAN_MAX = 16;
/**
 * World-space offset of the receiver along its normal, metres. Kept small: at 4.5 deg a normal
 * offset of h moves a ground shadow's edge by h / tan(4.5) = 12.7 h, so it is the wrong tool
 * for grazing acne (the slope-scaled depth bias above is the right one) and stays here only
 * for the near-vertical receivers where it is cheap and exact.
 */
const SHADOW_NORMAL_BIAS = 0.015;

/**
 * Half-extent (m) and depth range of the cascade camera during a precompile. Wide enough for
 * the whole world *and* any warm-up geometry parked far below the map before a precompile: a
 * caster outside the shadow camera's near/far never compiles its shadow variant, which was six
 * `ShadowMaterial` pipelines arriving mid-flyby (2026-09-05). See WEBGPU-NOTES §1.
 */
const PRECOMPILE_SHADOW_HALF = 5000;
const PRECOMPILE_SHADOW_NEAR = 0.1;
const PRECOMPILE_SHADOW_FAR = 12000;

// --- following a moving key light ------------------------------------------------------
/**
 * The sun moves now. `render/sky.ts` sweeps a full arc every four real hours and hands the key
 * over to the moon (`keyDir = -sunDir`) once the sun is down; `render/scene.ts` writes that into
 * `sun.position` and `sun.intensity` once a frame.
 *
 * The *cascade cameras* already follow it for free — `CSMShadowNode.updateBefore()` re-reads
 * `light.position`/`light.target.position` every frame and re-aims each cascade. What does not
 * follow it for free is the slope-scaled bias's `N·L` term, whose light vector is a uniform this
 * file owns (`StillsunCSM.setSunDirection`). Left at install time it would hold a fixed
 * afternoon angle forever and the bias would be wrong by `tan(acos(N·L))` at every other hour.
 *
 * Refreshed on a cosine epsilon rather than every frame. 0.99995 is 0.57 degrees; the sun covers
 * ~0.00042 degrees per frame at 60 fps, so this fires roughly every 23 s. That is far finer than
 * the bias needs — half a degree of light movement changes `tan(acos(N·L))` by well under a
 * texel of depth except at grazing incidence, where `BIAS_SLOPE_TAN_MAX` has already clamped it.
 * It is a uniform write either way; the epsilon exists so the comparison, not the write, is what
 * runs 60 times a second.
 */
const SUN_DIR_EPSILON_COS = 0.99995;

/**
 * Night gate. Below this fraction of the brightest key intensity seen this session, the sun
 * cascade stops rendering and its contribution is taken to zero.
 *
 * 0.20 / 0.25 with hysteresis, and the numbers come from the sky: the moon's key sits at ~0.16
 * of the design sun, so a full night is comfortably under the lower edge and the pass is skipped
 * from dusk to dawn; the sun clears the horizon gate well above the upper edge, so shadows are
 * back before there is a shadow worth seeing. The band stops a key hovering exactly on the line
 * (a storm, a scripted dim) from starting and stopping the pass on alternate frames.
 *
 * **Intensity and flags only — never `visible`, never the light count.** Three hashes the
 * *visible* light set into every material's cache key, so switching a light off rebuilds every
 * material in the scene (WEBGPU-NOTES §3, PERF-BUDGET rule 3). Both levers used here are safe:
 * `LightShadow.intensity` is a `reference('intensity', 'float', shadow)` uniform inside
 * `ShadowNode` (three.webgpu.js:45136), and `LightShadow.autoUpdate` is a plain JS flag
 * `ShadowNode.updateBefore` reads to decide whether to render the map at all (:45484). Neither
 * is a shader input. The light stays in the scene, lit, at whatever intensity the sky says.
 */
const SHADOW_KEY_OFF = 0.2;
const SHADOW_KEY_ON = 0.25;

/**
 * `CSMShadowNode` clones `light.shadow` for its cascade at `_init` and `LightShadow.copy` does
 * not carry `biasNode`, so the slope-scaled bias has to be attached to the clone after the
 * fact. With one cascade the per-cascade bookkeeping is a single uniform rather than an array,
 * but the bias itself stays slope-scaled — the acne arithmetic above does not care how many
 * cascades there are.
 */
class StillsunCSM extends CSMShadowNode {
  private mapSize = 2048;
  /** World metres per shadow-map texel (see `installCascadeBias`). */
  private texelUniform: AnyNode = null;
  private sunDirUniform: AnyNode = null;

  // Not in the typings: a private hook on the JS class. Kept minimal on purpose.
  _init(builder: AnyNode): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    (CSMShadowNode.prototype as unknown as { _init(b: AnyNode): void })._init.call(this, builder);
    for (const l of this.lights) {
      const shadow = l.shadow as { mapSize: { set(w: number, h: number): void } } | undefined;
      if (shadow === undefined) continue;
      // scene.ts's `setMap` bumps the *sun's* map to 4096 on large maps, which was right for
      // one map over the whole world; the cascade keeps this file's size regardless of what
      // the source shadow says at clone time.
      shadow.mapSize.set(this.mapSize, this.mapSize);
    }
    this.installCascadeBias();
  }

  setMapSize(size: number): void {
    this.mapSize = size;
  }

  setSunDirection(dir: Vector3): void {
    if (this.sunDirUniform === null) this.sunDirUniform = uniform(dir.clone().normalize());
    else (this.sunDirUniform.value as Vector3).copy(dir).normalize();
  }

  /**
   * Slope-scaled depth bias, attached to the cascade's cloned shadow as a `biasNode` (three:
   * `shadow.biasNode || reference('bias')`) and evaluated per receiver pixel. Negative: three
   * adds the bias to the receiver's depth and a lit test is map >= receiver, so a negative
   * value pulls the receiver toward the light.
   *
   *   bias = -(texel * (BIAS_BASE_TEXELS + BIAS_SLOPE_TEXELS * min(tan(acos(N.L)), TAN_MAX)))
   *          / (far - near)
   */
  installCascadeBias(): void {
    if (this.sunDirUniform === null) return;
    const l: AnyNode = this.sunDirUniform;
    const shadow = this.lights[0]?.shadow as
      { biasNode?: AnyNode; camera: { near: number; far: number } } | undefined;
    if (shadow === undefined) return;
    const uTexel = uniform(1);
    this.texelUniform = uTexel;
    const depthRange = Math.max(1, shadow.camera.far - shadow.camera.near);
    const ndl = clamp(normalWorld.dot(l), 0.0001, 1);
    const tanTheta = min(sqrt(float(1).sub(ndl.mul(ndl))).div(ndl), BIAS_SLOPE_TAN_MAX);
    const metres = uTexel.mul(float(BIAS_BASE_TEXELS).add(tanTheta.mul(BIAS_SLOPE_TEXELS)));
    shadow.biasNode = metres.div(depthRange).negate();
    this.refreshTexels();
  }

  /** Re-reads the cascade's ortho extent into the texel uniform. Cheap; call after updateFrustums. */
  refreshTexels(): void {
    const u = this.texelUniform;
    const cam = this.lights[0]?.shadow?.camera as { left: number; right: number } | undefined;
    if (u === null || cam === undefined) return;
    u.value = (cam.right - cam.left) / this.mapSize;
  }
}

export interface ShadowHandle {
  readonly csm: CSMShadowNode | null;
  readonly params: ShadowParams;
  dispose(): void;
}

/**
 * Configures `sun` for its single sun cascade. Call once, right after the light is created and
 * before anything compiles; the cascade sizes itself to the camera on first compile and follows
 * its FOV from then on.
 *
 * @param tier Accepted so the call site in `scene.ts` does not have to change; every tier gets
 *             the same shadow now (see `PARAMS`).
 */
export function installSunShadows(sun: DirectionalLight, tier: QualityTier): ShadowHandle {
  void tier;
  const params = PARAMS;
  sun.castShadow = true;
  sun.shadow.mapSize.set(params.mapSize, params.mapSize);
  sun.shadow.bias = 0; // replaced by the slope-scaled biasNode in StillsunCSM
  sun.shadow.normalBias = SHADOW_NORMAL_BIAS;
  sun.shadow.radius = params.radius;
  // The cascade camera clones this one; its ortho extents are overwritten per frame but the
  // near/far range is what the depth bias above is scaled against.
  sun.shadow.camera.near = 1;
  sun.shadow.camera.far = 600;
  // Deliberately no `layers.enable(RIG_LAYER)`: characters do not cast shadow maps.
  sun.shadow.camera.updateProjectionMatrix();

  let csm: StillsunCSM | null = null;
  // `?csm=0`: keep the single map scene.ts configured, for attributing shader-compile time and
  // frame cost to the cascade machinery.
  if (queryDisabled('csm')) {
    console.info('[stillsun] sun shadows: cascade off (?csm=0), single map');
    return { csm: null, params, dispose() {} };
  }
  try {
    csm = new StillsunCSM(sun, {
      cascades: params.cascades,
      maxFar: params.maxFar,
      mode: 'custom',
      // The light sits this far behind the cascade's bounding box: anything taller than this
      // behind the camera would otherwise be clipped from casting.
      lightMargin: 300,
      customSplitsCallback: (_cascades, _near, far, breaks) => {
        for (const s of params.splits) breaks.push(Math.min(1, s / far));
      },
    });
    csm.fade = false; // one cascade: no boundary to blend across
    csm.setMapSize(params.mapSize);
    csm.setSunDirection(sun.position.clone().sub(sun.target.position));
    (sun.shadow as { shadowNode?: unknown }).shadowNode = csm;
  } catch (err) {
    console.warn(
      '[stillsun] cascaded shadows unavailable; falling back to the single sun map',
      err,
    );
    csm = null;
  }

  // Casting is switched by writing every cascade's shadow `intensity` and `autoUpdate` — the
  // two levers that are not shader inputs. See SHADOW_KEY_OFF for why nothing here touches
  // `visible` or `castShadow`.
  type GateableShadow = { intensity: number; autoUpdate: boolean };
  let casting = true;
  const setCasting = (on: boolean): void => {
    casting = on;
    (sun.shadow as unknown as GateableShadow).intensity = on ? 1 : 0;
    (sun.shadow as unknown as GateableShadow).autoUpdate = on;
    if (csm === null) return;
    for (const l of csm.lights) {
      const shadow = l.shadow as unknown as GateableShadow | undefined;
      if (shadow === undefined) continue;
      shadow.intensity = on ? 1 : 0;
      shadow.autoUpdate = on;
    }
  };

  // Follow the camera's projection and the key light. `updateFrustums` is CPU-cheap (a few dozen
  // vector ops) but rewrites the cascade's ortho box, so only on an actual change.
  const seen = new Vector3(-1, -1, -1); // fov, aspect, far
  const seenSunDir = new Vector3(0, 0, 0);
  const keyDir = new Vector3();
  // The brightest key seen this session, as the reference the gate is a fraction of. A
  // high-water mark rather than a constant because this file must not import sky.ts and has no
  // other way to know what "full sun" is worth; it self-corrects within one day cycle, and the
  // direction it is wrong in on a cold night start is "shadows stay on", which is the safe one.
  let peakIntensity = Math.max(1e-3, sun.intensity);
  const stop = onBeforeFrame(() => {
    if (csm === null) return;

    // 1. The key light's direction, for the slope-scaled bias. See SUN_DIR_EPSILON_COS.
    keyDir.copy(sun.position).sub(sun.target.position);
    const len = keyDir.length();
    if (len > 1e-6) {
      keyDir.divideScalar(len);
      if (keyDir.dot(seenSunDir) < SUN_DIR_EPSILON_COS) {
        seenSunDir.copy(keyDir);
        // A uniform write; `setSunDirection` copies, so the scratch vector is safe to reuse.
        csm.setSunDirection(keyDir);
      }
    }

    // 2. The night gate.
    if (sun.intensity > peakIntensity) peakIntensity = sun.intensity;
    const keyFrac = sun.intensity / peakIntensity;
    if (casting ? keyFrac < SHADOW_KEY_OFF : keyFrac >= SHADOW_KEY_ON) setCasting(!casting);
    // Nothing below this line is worth doing while the pass is off: the cascade is not being
    // rendered and its contribution is zero, so its frusta can wait for dawn.
    if (!casting) return;

    // 3. The view frustum.
    if (csm.camera === null) return;
    const cam = csm.camera as unknown as { fov?: number; aspect?: number; far: number };
    const fov = cam.fov ?? 0;
    const aspect = cam.aspect ?? 1;
    if (seen.x === fov && seen.y === aspect && seen.z === cam.far) return;
    seen.set(fov, aspect, cam.far);
    csm.updateFrustums();
    csm.refreshTexels();
  });

  // While a precompile runs, the cascade covers the whole world, so casters that happen to sit
  // outside its frustum at that moment still get their shadow-pass pipeline built (measured:
  // extract beacons and region meshes compiled on first entering view). WEBGPU-NOTES §1.
  // updateFrustums() puts the real extents back afterwards.
  const stopPrecompile = onPrecompile((phase) => {
    if (csm === null) return;
    if (phase === 'begin') {
      // A precompile that runs at night would otherwise build no shadow-pass variants at all
      // (the gate has `autoUpdate` off), and they would compile on screen at dawn — the exact
      // invisible stall WEBGPU-NOTES §1 is about. Forced on for the compile; the gate re-reads
      // the key on the very next frame and switches it back off if it is still dark.
      setCasting(true);
      for (const l of csm.lights) {
        const cam = l.shadow?.camera as
          | {
              left: number;
              right: number;
              top: number;
              bottom: number;
              near: number;
              far: number;
              updateProjectionMatrix(): void;
            }
          | undefined;
        if (cam === undefined) continue;
        cam.left = -PRECOMPILE_SHADOW_HALF;
        cam.right = PRECOMPILE_SHADOW_HALF;
        cam.top = PRECOMPILE_SHADOW_HALF;
        cam.bottom = -PRECOMPILE_SHADOW_HALF;
        cam.near = PRECOMPILE_SHADOW_NEAR;
        cam.far = PRECOMPILE_SHADOW_FAR;
        cam.updateProjectionMatrix();
      }
    } else {
      // Extents come back from updateFrustums(); near/far are ours to put back.
      for (const l of csm.lights) {
        const cam = l.shadow?.camera as
          { near: number; far: number; updateProjectionMatrix(): void } | undefined;
        if (cam === undefined) continue;
        cam.near = sun.shadow.camera.near;
        cam.far = sun.shadow.camera.far;
        cam.updateProjectionMatrix();
      }
      if (csm.camera !== null) {
        csm.updateFrustums();
        csm.refreshTexels();
      }
    }
  });

  console.info(
    `[stillsun] sun shadows: ${csm !== null ? '1 cascade' : 'single map'} · ` +
      `${String(params.mapSize)}px · to ${String(params.maxFar)} m · radius ${String(params.radius)}`,
  );

  return {
    csm,
    params,
    dispose() {
      stop();
      stopPrecompile();
      csm?.dispose();
    },
  };
}
